<div fxLayout="column" fxFlex fxLayoutGap="24px">
  <!-- Header -->
  <div fxLayout="row" fxLayoutAlign="space-between center">
    <h2>{{ create ? 'Create' : 'Edit' }} Credential Configuration</h2>
    <div fxLayout="row" fxLayoutGap="8px">
      <!-- JSON View Button -->
      <button mat-stroked-button (click)="viewAsJson()" matTooltip="View/Edit as JSON">
        <mat-icon>code</mat-icon>
        JSON View
      </button>

      <!-- Predefined Configs Menu -->
      @if (create) {
        <button
          mat-stroked-button
          [matMenuTriggerFor]="templatesMenu"
          matTooltip="Load predefined configuration"
        >
          <mat-icon>template_add</mat-icon>
          Templates
        </button>
        <mat-menu #templatesMenu="matMenu">
          @for (template of predefinedConfigs; track template.name) {
            <div>
              <button mat-menu-item (click)="loadPredefinedConfig(template)">
                <mat-icon>{{ template.icon }}</mat-icon>
                <div fxLayout="column" fxLayoutAlign="start start">
                  <span>{{ template.name }}</span>
                  <small class="menu-description">{{ template.description }}</small>
                </div>
              </button>
              <button mat-menu-item (click)="previewPredefinedConfig(template)">
                <mat-icon>visibility</mat-icon>
                <span>Preview {{ template.name }}</span>
              </button>
              @if (!$last) {
                <mat-divider></mat-divider>
              }
            </div>
          }
        </mat-menu>
      }

      <!-- Back Button -->
      <button mat-icon-button [routerLink]="['../']" matTooltip="Back to list">
        <mat-icon>arrow_back</mat-icon>
      </button>
    </div>
  </div>

  <!-- Main Form -->
  <form [formGroup]="form" (ngSubmit)="onSubmit()">
    <!-- Basic Information Card -->
    <mat-card>
      <mat-card-header>
        <mat-card-title>
          <mat-icon>settings</mat-icon>
          Basic Information
        </mat-card-title>
      </mat-card-header>
      <mat-card-content fxLayout="column" fxLayoutGap="16px">
        <!-- Configuration ID -->
        <mat-form-field>
          <mat-label>Configuration ID</mat-label>
          <input matInput formControlName="id" placeholder="unique-credential-config-id" />
          <mat-icon matSuffix>fingerprint</mat-icon>
          @if (form.get('id')?.invalid && form.get('id')?.touched) {
            <mat-error>Configuration ID is required</mat-error>
          }
        </mat-form-field>

        <mat-form-field>
          <mat-label>Description</mat-label>
          <input matInput formControlName="description" placeholder="Description" />
          <mat-icon matSuffix>description</mat-icon>
          @if (form.get('description')?.invalid && form.get('description')?.touched) {
            <mat-error>Description is required</mat-error>
          }
        </mat-form-field>

        <!-- Key Selection -->
        <mat-form-field>
          <mat-label>Signing Key</mat-label>
          <mat-select formControlName="keyId">
            <mat-option value="">
              <em>No key selected</em>
            </mat-option>
            @for (key of keys; track key.id) {
              <mat-option [value]="key.id">
                <div fxLayout="column">
                  <span
                    ><strong>{{ key.id }}</strong></span
                  >
                  <span class="option-description">{{ key.description }}</span>
                </div>
              </mat-option>
            }
          </mat-select>
          <mat-icon matSuffix>vpn_key</mat-icon>
          <mat-hint>Select the cryptographic key for signing credentials</mat-hint>
        </mat-form-field>

        <!-- Lifetime -->
        <mat-form-field>
          <mat-label>Credential Lifetime (seconds)</mat-label>
          <input matInput type="number" formControlName="lifeTime" placeholder="3600" min="1" />
          <mat-icon matSuffix>schedule</mat-icon>
          <mat-hint>How long the credential remains valid (e.g., 3600 = 1 hour)</mat-hint>
        </mat-form-field>

        <!-- Scope -->
        <mat-form-field>
          <mat-label>Scope</mat-label>
          <input matInput formControlName="scope" placeholder="scope" />
          <mat-hint>Select the scope for the credential</mat-hint>
        </mat-form-field>
      </mat-card-content>
    </mat-card>

    <!-- Feature Configuration Card -->
    <mat-card>
      <mat-card-header>
        <mat-card-title>
          <mat-icon>tune</mat-icon>
          Feature Configuration
        </mat-card-title>
      </mat-card-header>
      <mat-card-content fxLayout="column" fxLayoutGap="16px">
        <!-- Feature Toggles -->
        <div fxLayout="row" fxLayoutGap="32px" fxLayout.lt-md="column">
          <div fxFlex>
            <mat-slide-toggle formControlName="keyBinding">
              <div fxLayout="column">
                <span><strong>Key Binding</strong></span>
                <span class="toggle-description">Enable to issue key-bound credentials</span>
              </div>
            </mat-slide-toggle>
          </div>
          <div fxFlex>
            <mat-slide-toggle formControlName="statusManagement">
              <div fxLayout="column">
                <span><strong>Status Management</strong></span>
                <span class="toggle-description">Enable credential status tracking</span>
              </div>
            </mat-slide-toggle>
          </div>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Display Configuration Card -->
    <mat-card>
      <mat-card-header>
        <mat-card-title>
          <mat-icon>palette</mat-icon>
          Display Configuration
        </mat-card-title>
        <mat-card-subtitle>
          Configure how credentials appear in wallets and viewers
        </mat-card-subtitle>
      </mat-card-header>
      <mat-card-content>
        <div formArrayName="displayConfigs">
          @for (displayConfig of displayConfigs.controls; track $index; let i = $index) {
            <mat-card class="display-config-card" [formGroupName]="i">
              <mat-card-header>
                <mat-card-title>
                  <span>Display Configuration {{ i + 1 }}</span>
                  @if (displayConfigs.length > 1) {
                    <button
                      mat-icon-button
                      type="button"
                      color="warn"
                      (click)="removeDisplayConfig(i)"
                      matTooltip="Remove this display configuration"
                    >
                      <mat-icon>delete</mat-icon>
                    </button>
                  }
                </mat-card-title>
              </mat-card-header>
              <mat-card-content fxLayout="column" fxLayoutGap="16px">
                <!-- Basic Display Information -->
                <div fxLayout="row" fxLayoutGap="16px" fxLayout.lt-md="column">
                  <mat-form-field fxFlex>
                    <mat-label>Display Name</mat-label>
                    <input matInput formControlName="name" placeholder="PID" required />
                    <mat-icon matSuffix>title</mat-icon>
                    <mat-hint>Short name shown in wallet</mat-hint>
                  </mat-form-field>

                  <mat-form-field fxFlex>
                    <mat-label>Locale</mat-label>
                    <mat-select formControlName="locale">
                      <mat-option value="en-US">English (US)</mat-option>
                      <mat-option value="en-GB">English (UK)</mat-option>
                      <mat-option value="de-DE">German</mat-option>
                      <mat-option value="fr-FR">French</mat-option>
                      <mat-option value="es-ES">Spanish</mat-option>
                      <mat-option value="it-IT">Italian</mat-option>
                    </mat-select>
                    <mat-icon matSuffix>language</mat-icon>
                  </mat-form-field>
                </div>

                <!-- Description -->
                <mat-form-field>
                  <mat-label>Description</mat-label>
                  <input
                    matInput
                    formControlName="description"
                    placeholder="PID Credential"
                    required
                  />
                  <mat-icon matSuffix>description</mat-icon>
                  <mat-hint>Detailed description of the credential</mat-hint>
                </mat-form-field>

                <!-- Colors -->
                <div fxLayout="row" fxLayoutGap="16px" fxLayout.lt-md="column">
                  <mat-form-field fxFlex>
                    <mat-label>Background Color</mat-label>
                    <input
                      matInput
                      formControlName="background_color"
                      placeholder="#FFFFFF"
                      pattern="^#[0-9A-Fa-f]{6}$"
                    />
                    <mat-icon matSuffix>palette</mat-icon>
                    <mat-hint>Hex color code (e.g., #FFFFFF)</mat-hint>
                  </mat-form-field>

                  <mat-form-field fxFlex>
                    <mat-label>Text Color</mat-label>
                    <input
                      matInput
                      formControlName="text_color"
                      placeholder="#000000"
                      pattern="^#[0-9A-Fa-f]{6}$"
                    />
                    <mat-icon matSuffix>format_color_text</mat-icon>
                    <mat-hint>Hex color code (e.g., #000000)</mat-hint>
                  </mat-form-field>
                </div>

                <!-- Background Image -->

                <app-image-field
                  [field]="getControl(displayConfig.get('background_image.uri'))"
                  label="Background Image"
                  [required]="false"
                ></app-image-field>

                <app-image-field
                  [field]="getControl(displayConfig.get('logo.uri'))"
                  label="Logo"
                  [required]="false"
                ></app-image-field>
              </mat-card-content>
            </mat-card>
          }
        </div>

        <!-- Add Display Configuration Button -->
        <div fxLayout="row" fxLayoutAlign="center center" class="add-button-container">
          <button mat-stroked-button type="button" (click)="addDisplayConfig()">
            <mat-icon>add</mat-icon>
            Add Display Configuration
          </button>
        </div>
      </mat-card-content>
    </mat-card>

    <mat-card>
      <mat-card-header>
        <mat-card-title>
          <mat-icon>webhook</mat-icon>
          Claim Webhook (Optional)
        </mat-card-title>
        <mat-card-subtitle>URL to receive claim data during issuance process</mat-card-subtitle>
      </mat-card-header>
      <mat-card-content>
        <app-webhook-config-edit [group]="getFormGroup('claimsWebhook')"></app-webhook-config-edit>
      </mat-card-content>
    </mat-card>

    <mat-card>
      <mat-card-header>
        <mat-card-title>
          <mat-icon>webhook</mat-icon>
          Notification Webhook (Optional)
        </mat-card-title>
        <mat-card-subtitle>URL to receive notifications during issuance process</mat-card-subtitle>
      </mat-card-header>
      <mat-card-content>
        <app-webhook-config-edit
          [group]="getFormGroup('notificationWebhook')"
        ></app-webhook-config-edit>
      </mat-card-content>
    </mat-card>

    <!-- VCT Information Card -->
    <mat-card>
      <mat-card-header>
        <mat-card-title>
          <mat-icon>description</mat-icon>
          Verifiable Credential Type (VCT)
        </mat-card-title>
      </mat-card-header>
      <mat-card-content fxLayout="column" fxLayoutGap="16px">
        <app-editor
          [schema]="vctSchema"
          formControlName="vct"
          [errors]="form.get('vct')?.errors"
        ></app-editor>
      </mat-card-content>
    </mat-card>

    <!-- Advanced Configuration -->
    <mat-expansion-panel>
      <mat-expansion-panel-header>
        <mat-panel-title>
          <mat-icon>settings_applications</mat-icon>
          Advanced Configuration
        </mat-panel-title>
        <mat-panel-description> Optional advanced settings and schemas </mat-panel-description>
      </mat-expansion-panel-header>

      <div fxLayout="column" fxLayoutGap="16px" class="expansion-content">
        <!-- Default Claims -->
        <h4>Default claims to include in every credential</h4>
        <app-editor formControlName="claims" [errors]="form.get('claims')?.errors"></app-editor>

        <!-- Disclosure Frame -->
        <h4>Disclosure Frame (SD-JWT)</h4>
        <app-editor
          formControlName="disclosureFrame"
          [errors]="form.get('disclosureFrame')?.errors"
        ></app-editor>

        <!-- JSON Schema -->
        <h4>JSON Schema</h4>
        <app-editor formControlName="schema" [errors]="form.get('schema')?.errors"></app-editor>

        <!-- Embedded Disclosure Policy -->
        <h4>Embedded Disclosure Policy</h4>
        <app-editor
          [schema]="embeddedDisclosurePolicySchema"
          formControlName="embeddedDisclosurePolicy"
          [errors]="form.get('embeddedDisclosurePolicy')?.errors"
        ></app-editor>
      </div>
    </mat-expansion-panel>

    <!-- Action Buttons -->
    <div fxLayout="row" fxLayoutGap="16px" fxLayoutAlign="end center">
      <button mat-button type="button" [routerLink]="['../']">Cancel</button>
      <button mat-flat-button type="submit" [disabled]="form.invalid || loading">
        @if (loading) {
          <mat-spinner diameter="20" class="button-spinner"></mat-spinner>
        }
        @if (!loading) {
          <mat-icon>{{ create ? 'add' : 'save' }}</mat-icon>
        }
        {{ create ? 'Create' : 'Save' }} Configuration
      </button>
    </div>
  </form>
</div>
